import React, { useState, useEffect, useCallback } from 'react';
import { myCompensationAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, DollarSign, TrendingUp, Receipt, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const fmtMoney = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '$0.00';
  return n < 0
    ? `-$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const STATUS_BADGE = {
  Imported: 'bg-slate-100 text-slate-700',
  Pending_Review: 'bg-amber-100 text-amber-800',
  Accounting_Reviewed: 'bg-sky-100 text-sky-800',
  Approved: 'bg-blue-100 text-blue-800',
  Scheduled: 'bg-purple-100 text-purple-800',
  Paid: 'bg-emerald-100 text-emerald-800',
  Voided: 'bg-rose-100 text-rose-800',
  Excluded: 'bg-zinc-100 text-zinc-600',
};

/**
 * Compensation tab for /my-payouts. Surfaces the per-record compensation
 * detail that the new Compensation Engine produces, filtered to the calling
 * user's role/context by the `/api/compensation/my-compensation` endpoint.
 */
export default function MyCompensationTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await myCompensationAPI.get(month === 'all' ? undefined : month);
      setData(r.data);
    } catch (err) {
      if (err?.response?.status !== 403) {
        toast.error(err?.response?.data?.detail || 'Failed to load compensation');
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="py-8 text-center">
          <AlertCircle className="w-8 h-8 mx-auto text-amber-600 mb-2" />
          <p className="text-sm text-amber-900">
            No compensation visibility for your role. If you believe this is wrong,
            contact KR Accounting.
          </p>
        </CardContent>
      </Card>
    );
  }

  const { records = [], total_records = 0, my_payouts_total = 0, expected_total = 0,
          actual_total = 0, by_status = {}, available_months = [],
          my_payee_type } = data;

  const payeeLabel = my_payee_type === 'Agent'
    ? 'Your share'
    : my_payee_type === 'Agency'
      ? 'Your agency share'
      : 'Total compensation';

  return (
    <div className="space-y-6" data-testid="my-compensation-tab">
      {/* Month filter */}
      <div className="flex items-center justify-end gap-3">
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-44" data-testid="my-compensation-month">
            <SelectValue placeholder="All Months" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Months</SelectItem>
            {available_months.map((m) => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">{payeeLabel}</CardTitle>
            <DollarSign className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600" data-testid="my-comp-mine">
              {fmtMoney(my_payouts_total)}
            </div>
            <p className="text-xs text-gray-500 mt-1">Across {total_records} records</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Expected</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{fmtMoney(expected_total)}</div>
            <p className="text-xs text-gray-500 mt-1">Sum of carrier expected</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Actual Paid</CardTitle>
            <Receipt className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{fmtMoney(actual_total)}</div>
            <p className="text-xs text-gray-500 mt-1">Sum of carrier actual</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Variance</CardTitle>
            <AlertCircle className={`h-4 w-4 ${expected_total - actual_total > 0 ? 'text-rose-500' : 'text-emerald-500'}`} />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${expected_total - actual_total > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
              {fmtMoney(expected_total - actual_total)}
            </div>
            <p className="text-xs text-gray-500 mt-1">Expected − Actual</p>
          </CardContent>
        </Card>
      </div>

      {/* Status breakdown */}
      {Object.keys(by_status).length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Records by status</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2" data-testid="my-comp-status-breakdown">
            {Object.entries(by_status).map(([status, count]) => (
              <Badge key={status} className={STATUS_BADGE[status] || 'bg-gray-100 text-gray-700'}>
                {status} · {count}
              </Badge>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Records table */}
      <Card>
        <CardHeader>
          <CardTitle>Compensation Records</CardTitle>
          <CardDescription>
            Per-policy detail of what the carrier paid vs the compensation routed to you.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {records.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-500">
              No compensation records yet for this filter.
            </div>
          ) : (
            <div className="overflow-auto max-h-[60vh]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Policy Holder</TableHead>
                    <TableHead>Carrier</TableHead>
                    <TableHead>Writing Agent</TableHead>
                    <TableHead className="text-right">Expected</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    <TableHead className="text-right">Your share</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((r) => {
                    const mine = (r.payouts || []).find(
                      (p) =>
                        my_payee_type === null ||
                        my_payee_type === undefined ||
                        p.payee_type === my_payee_type
                    );
                    const mineAmt = my_payee_type
                      ? mine?.amount ?? 0
                      : r.final_compensation ?? 0;
                    return (
                      <TableRow key={r.record_id} data-testid={`my-comp-row-${r.record_id}`}>
                        <TableCell className="text-sm">{r.statement_month || '—'}</TableCell>
                        <TableCell className="text-sm max-w-[180px] truncate" title={r.policy_holder}>
                          {r.policy_holder || '—'}
                        </TableCell>
                        <TableCell className="text-sm">{r.carrier_name || r.carrier_id || '—'}</TableCell>
                        <TableCell className="text-sm">{r.writing_agent_name || r.writing_agent_npn || '—'}</TableCell>
                        <TableCell className="text-right">{fmtMoney(r.expected_amount)}</TableCell>
                        <TableCell className="text-right">{fmtMoney(r.actual_carrier_paid)}</TableCell>
                        <TableCell className="text-right font-semibold text-emerald-600">
                          {fmtMoney(mineAmt)}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_BADGE[r.status] || 'bg-gray-100'}>
                            {r.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
